import { MaterialIcons } from '@expo/vector-icons';
import {
  DayIntervalUnit,
  Issue,
  Workflow,
  cacheKeys,
  calculateDateFromInterval,
  calculateDaysDifference,
  calculateHoursDifference,
  calculateMinutesDifference,
  formatDateToMMDDYYYY,
  useDeleteMutation,
  useUpdateMutation,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Colors } from '../../themes';
import { getReminderDefaultValues } from '../../utils/reminderUtils';
import { IssueReminderModal } from './issueReminderModal';

type ReminderSummaryProps = {
  workflow: Workflow;
  issue: Issue;
};

export const ReminderSummary = ({ workflow, issue }: ReminderSummaryProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isReminderModalVisible, setIsReminderModalVisible] = useState(false);
  const { id: workflowId, trigger } = workflow;
  const excludedConfigKeys = ['dayIntervalUnit', 'issueId', 'startDate'];
  const configKeys = Object.keys(trigger.config)
    .filter(
      (key) => !excludedConfigKeys.includes(key)
    );

  const { mutateAsync: updateWorkflow } = useUpdateMutation({
    modelName: 'workflow',
    query: UPDATE_WORKFLOW,
  });

  const { mutateAsync: deleteWorkflow } = useDeleteMutation({
    modelName: 'workflow',
    query: DELETE_WORKFLOW,
  });

  const defaultValues = useMemo(() => getReminderDefaultValues(workflow), [workflow]);

  let value;
  let dateLabel;
  let daysToGoCount;
  let daysToGoLabel;
  let alertDate = new Date();
  let startDate = new Date();
  const now = new Date();

  // label ex:  09/17/2025 (16 days to go)
  switch (configKeys[0]) {
    case 'onDate':
      if (
        trigger?.config?.dayInterval && 
        trigger?.config?.dayIntervalUnit
      ) {
        const now = new Date();
        const interval = trigger.config.dayInterval;
        const unit = trigger.config.dayIntervalUnit;
        const onDate = trigger.config.onDate || now.toISOString();
        const alertDateStr = calculateDateFromInterval(interval, unit, new Date(onDate)).toISOString();
        dateLabel = formatDateToMMDDYYYY(alertDateStr);
        
        switch (unit) {
          case DayIntervalUnit.Minutes: {
            dateLabel = formatDateToMMDDYYYY(alertDateStr);
            const minDiff = calculateMinutesDifference(
              alertDateStr,
              now
            );

            daysToGoLabel = `${dateLabel} (${minDiff > 0 ? minDiff : 0} minutes to go)`;  
            break;
          }
          case DayIntervalUnit.Hours: {
            const hourDiff = calculateHoursDifference(
              alertDateStr,
              now
            );
            daysToGoLabel = `${dateLabel} (${hourDiff > 0 ? hourDiff : 0} hours to go)`;
            break;
          }
          default: {
            daysToGoCount = calculateDaysDifference(
              alertDateStr,
              now
            );
            daysToGoLabel = `${dateLabel} (${
              daysToGoCount > 0 ? daysToGoCount : 0
            } days to go)`;  
            break;
          }
        }
      } else {
        value = trigger.config.onDate || now;
        dateLabel = formatDateToMMDDYYYY(value);

        alertDate = new Date(value);
        daysToGoCount = calculateDaysDifference(alertDate, now);

        daysToGoLabel = `${dateLabel} (${
                daysToGoCount > 0 ? daysToGoCount : 0
              } days to go)`;  
      }
      break;

    case 'every':
      value = Number(trigger.config.every) || 0;
      startDate = trigger?.config?.startDate
        ? new Date(trigger.config.startDate)
        : now;

      alertDate.setDate(startDate.getDate() + value);

      while (alertDate < now) {
        alertDate.setDate(alertDate.getDate() + value);
      }

      dateLabel = formatDateToMMDDYYYY(alertDate);
      daysToGoCount = calculateDaysDifference(alertDate, now);

      daysToGoLabel = `${dateLabel} (${
              daysToGoCount > 0 ? daysToGoCount : 0
            } days to go)`;  

      break;

    default:
      dateLabel = 'No date available...';
      daysToGoCount = 0;

      daysToGoLabel = `${dateLabel} (${
              daysToGoCount > 0 ? daysToGoCount : 0
            } days to go)`;  
      break;
  }

  const submitReminder = async (input: any) => {
    input = { ...input, id: workflowId };

    await updateWorkflow(
      { input },
      {
        onSuccess: async () => {
          setIsReminderModalVisible(false);
        },
      }
    );
  };

  const deleteReminder = async () => {
    const input = {
      id: workflowId,
    };

    await deleteWorkflow(
      { input },
      {
        onSuccess: () => {
          setIsReminderModalVisible(false);
          
          // Refresh the workflow cache to show the updated reminder
          // This will trigger a re-render and show the updated reminder in the UI
          queryClient.invalidateQueries({ queryKey: cacheKeys('workflow').all });
          queryClient.invalidateQueries({ queryKey: cacheKeys('issue').all });
        },
      }
    );
  };

  return (
    <>
      <Pressable
        style={styles.reminderContainer}
        onPress={() => {
          setIsReminderModalVisible(true);
        }}
      >
        <View style={styles.subtitleRow}>
          <MaterialIcons
            name="alarm-on"
            size={16}
            style={{ color: Colors.grayText }}
          />
          <Text style={styles.subtitle}>{t('issue.reminder.scheduled')}</Text>
        </View>
        <View style={styles.titleRow}>
          <Text style={styles.titleLabel}>{daysToGoLabel}</Text>
          <MaterialIcons name="chevron-right" size={24} />
        </View>
      </Pressable>
      <IssueReminderModal
        visible={isReminderModalVisible}
        hide={() => setIsReminderModalVisible(false)}
        issue={issue}
        defaultValues={defaultValues}
        submitReminder={submitReminder}
        isEditReminder
        deleteReminder={deleteReminder}
        onSuccess={() => {
          // Refresh the workflow cache to show the updated reminder
          // This will trigger a re-render and show the updated reminder in the UI
          queryClient.invalidateQueries({ queryKey: cacheKeys('workflow').all });
          queryClient.invalidateQueries({ queryKey: cacheKeys('issue').all });
        }}
      />
    </>
  );
};

const styles = StyleSheet.create({
  reminderContainer: {
    borderWidth: 1,
    borderColor: Colors.secondaryGreyBorder,
    backgroundColor: Colors.secondaryGreyBackground,
    borderRadius: 12,
    height: 72,
    marginHorizontal: 20,
    marginBottom: 20,
    padding: 16,
  },
  subtitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 4,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 12,
    fontWeight: '500',
    letterSpacing: -0.25,
    color: Colors.grayText,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleLabel: {
    color: Colors.blackText,
    fontWeight: '500',
    fontSize: 16,
    letterSpacing: -0.25,
  },
});

const UPDATE_WORKFLOW = `
  mutation UpdateWorkflow($input: MutateWorkflowInput!) {
  updateWorkflow(input: $input) {
    id
  }
} 
`;

const DELETE_WORKFLOW = `
  mutation DeleteWorkflow($input: MutateWorkflowInput!) {
    deleteWorkflow(input: $input) {
      id
    }
  }
`;
